import axios from 'axios';
import React, { useRef, useState, useEffect } from 'react';
import { useLoginStore } from '../../stores/AuthStatusStore';
import ReCAPTCHA from 'react-google-recaptcha';
import LoginUsuarioExterno from '../../components/UsuarioExterno/LoginUsuario';

const validarCPF = (cpf: string) => {
  const strCPF = cpf.replace(/[^\d]+/g, '');
  if (strCPF === '' || strCPF.length !== 11 || /^(\d)\1{10}$/.test(strCPF))
    return false;
  let soma = 0;
  let resto;
  for (let i = 1; i <= 9; i++)
    soma += parseInt(strCPF.substring(i - 1, i)) * (11 - i);
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(strCPF.substring(9, 10))) return false;
  soma = 0;
  for (let i = 1; i <= 10; i++)
    soma += parseInt(strCPF.substring(i - 1, i)) * (12 - i);
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(strCPF.substring(10, 11))) return false;
  return true;
};

const organizacoesMock = [
  { id_organizacao: 1, desc_organizacao: 'OdontoGroup' },
  { id_organizacao: 2, desc_organizacao: 'Evo Saúde' },
  { id_organizacao: 3, desc_organizacao: 'Ambas' },
];

const initialFormState = {
  id_organizacao: 2,
  cpf: '',
  nome: '',
  id_motivo: '',
  is_beneficiario: 'Sim',
  is_corretor: 'Não',
};

export default function Blacklist(): JSX.Element {
  const isLogado = useLoginStore(x => x.login);
  const urlBase = process.env.REACT_APP_OG_API_URL;

  const [loadingState, setLoadingState] = useState<'idle' | 'processing'>(
    'idle',
  );
  const captchaRef = useRef<ReCAPTCHA>(null);
  const [recaptchaValido, setRecaptchaValido] = useState(false);
  const [showCaptcha, setShowCaptcha] = useState(false);

  const [form, setForm] = useState(initialFormState);
  const [motivosLista, setMotivosLista] = useState<
    { id_motivo: number; descricao_motivo: string }[]
  >([]);

  const [message, setMessage] = useState<{
    text: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);
  const [blacklistCheck, setBlacklistCheck] = useState<{
    status: 'idle' | 'checking' | 'listed' | 'not_listed' | 'error';
    text: string;
  }>({
    status: 'idle',
    text: '',
  });
  const [lastCheckedCpf, setLastCheckedCpf] = useState('');

  useEffect(() => {
    setShowCaptcha(false);
    if (isLogado) {
      const timer = setTimeout(() => {
        setShowCaptcha(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isLogado]);

  useEffect(() => {
    if (isLogado) {
      axios
        .get(`${urlBase}/motivos-blacklist`)
        .then(res =>
          setMotivosLista(
            res.data.filter(
              (m: any) => m.fl_ativo == 1 && m.cd_plataforma == 1,
            ),
          ),
        )
        .catch(err => console.error('Erro ao buscar motivos', err));
    }
  }, [isLogado, urlBase]);

  useEffect(() => {
    if (!isLogado) return;

    const cpfNumerico = form.cpf.replace(/\D/g, '');

    if (cpfNumerico.length !== 11 || !validarCPF(cpfNumerico)) {
      setBlacklistCheck({ status: 'idle', text: '' });
      setLastCheckedCpf('');
      return;
    }

    if (cpfNumerico === lastCheckedCpf) return;

    let isCancelled = false;
    setBlacklistCheck({
      status: 'checking',
      text: 'Consultando blacklist para este CPF...',
    });

    axios
      .post(`${urlBase}/blacklist/check`, { cpfCnpj: cpfNumerico })
      .then(res => {
        if (isCancelled) return;

        const isBlacklisted = !!res.data?.isBlacklisted;
        setBlacklistCheck({
          status: isBlacklisted ? 'listed' : 'not_listed',
          text: isBlacklisted
            ? 'Este CPF já está na blacklist.'
            : 'Este CPF não está na blacklist e está pronto para inclusão.',
        });
        setLastCheckedCpf(cpfNumerico);
      })
      .catch(err => {
        if (isCancelled) return;
        console.error('Erro ao checar blacklist', err);
        setBlacklistCheck({
          status: 'error',
          text: 'Não foi possível validar o CPF na blacklist agora. Tente novamente.',
        });
      });

    return () => {
      isCancelled = true;
    };
  }, [form.cpf, isLogado, lastCheckedCpf, urlBase]);

  const onCaptchaVerify = (token: string | null) => {
    setRecaptchaValido(!!token);
  };

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 11) value = value.slice(0, 11);

    value = value.replace(/(\d{3})(\d)/, '$1.$2');
    value = value.replace(/(\d{3})(\d)/, '$1.$2');
    value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2');

    setForm({ ...form, cpf: value });
    setMessage(null);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setMessage(null);
  };

  const handleRadioChange = (name: string, value: string) => {
    setForm(prev => {
      const oppositeValue = value === 'Sim' ? 'Não' : 'Sim';
      const oppositeName =
        name === 'is_beneficiario' ? 'is_corretor' : 'is_beneficiario';

      return {
        ...prev,
        [name]: value,
        [oppositeName]: oppositeValue,
      };
    });
  };

  const handleSubmit = async () => {
    if (!form.id_organizacao || !form.cpf || !form.nome || !form.id_motivo) {
      setMessage({
        text: 'Por favor, preencha todos os campos obrigatórios.',
        type: 'error',
      });
      return;
    }

    if (!validarCPF(form.cpf)) {
      setMessage({
        text: 'CPF inválido. Verifique os dígitos informados.',
        type: 'error',
      });
      return;
    }

    if (!recaptchaValido) {
      setMessage({
        text: 'Por favor, complete o reCAPTCHA para continuar.',
        type: 'error',
      });
      return;
    }

    try {
      setLoadingState('processing');
      setMessage({ text: 'Registrando bloqueio no sistema...', type: 'info' });

      const userStr = sessionStorage.getItem('usuarioLogado');
      const usuarioLogado = userStr ? JSON.parse(userStr) : null;

      if (!usuarioLogado || !usuarioLogado.id) {
        setMessage({
          text: 'Sessão inválida. Por favor, atualize a página e faça login novamente.',
          type: 'error',
        });
        setLoadingState('idle');
        return;
      }

      const payload = {
        ...form,
        cpf: form.cpf.replace(/\D/g, ''),
        isUsuarioExterno: 1,
        id_usuarioCadastro: usuarioLogado.id,
      };

      await axios.post(`${urlBase}/blacklist`, payload);

      setMessage({
        text: 'Registro incluído na Blacklist com sucesso!',
        type: 'success',
      });
      setForm(initialFormState);

      setRecaptchaValido(false);
      if (captchaRef.current) captchaRef.current.reset();
    } catch (err: any) {
      console.error(err);
      let errorMsg = 'Erro ao processar a solicitação.';
      if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      }
      setMessage({ text: errorMsg, type: 'error' });
    } finally {
      setLoadingState('idle');
    }
  };

  const limparCampos = () => {
    setForm(initialFormState);
    setMessage(null);
    setBlacklistCheck({ status: 'idle', text: '' });
    setLastCheckedCpf('');
    setRecaptchaValido(false);
    if (captchaRef.current) captchaRef.current.reset();
  };

  const inputStyle =
    'border border-gray-300 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-200 rounded-md p-2.5 w-full shadow-sm transition-all duration-300 text-gray-700 bg-white disabled:bg-gray-100';
  const labelStyle = 'block text-sm font-semibold text-gray-700 mb-1';

  return !isLogado ? (
    <div className="mx-auto my-auto py-20 rounded">
      <h2 className="text-[2rem] font-semibold text-primary-main mt-12 text-center">
        Blacklist
      </h2>
      <LoginUsuarioExterno />
    </div>
  ) : (
    <div className="mx-auto my-auto py-10 px-4">
      <div className="max-w-2xl mx-auto rounded-xl bg-white shadow-[0_10px_40px_rgba(0,0,0,0.08)] border-t-4 border-red-600 overflow-hidden">
        <div className="bg-gray-50 p-6 border-b border-gray-200">
          <div className="flex items-center justify-center gap-3 mb-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <h2 className="text-2xl font-bold text-gray-800">
              Inclusão em Blacklist
            </h2>
          </div>
          <p className="text-center text-gray-500 text-sm">
            Utilize este formulário para registrar um bloqueio.
          </p>
        </div>

        <div className="p-6 md:p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
            {/* <div className="col-span-1 md:col-span-2">
              <label className={labelStyle}>Operadora *</label>
              <select
                name="id_organizacao"
                value={form.id_organizacao}
                onChange={handleChange}
                disabled={loadingState !== 'idle'}
                className={inputStyle}
              >
                <option value="">Selecione a Operadora</option>
                {organizacoesMock.map(org => (
                  <option key={org.id_organizacao} value={org.id_organizacao}>
                    {org.desc_organizacao}
                  </option>
                ))}
              </select>
            </div> */}

            <div>
              <label className={labelStyle}>CPF *</label>
              <input
                type="text"
                name="cpf"
                value={form.cpf}
                onChange={handleCpfChange}
                disabled={loadingState !== 'idle'}
                className={inputStyle}
                placeholder="000.000.000-00"
                autoComplete="off"
              />
              {blacklistCheck.status !== 'idle' && (
                <div
                  className={`mt-2 p-2 rounded-md text-sm font-medium border
                    ${
                      blacklistCheck.status === 'listed'
                        ? 'bg-red-50 text-red-700 border-red-200'
                        : blacklistCheck.status === 'not_listed'
                        ? 'bg-green-50 text-green-700 border-green-200'
                        : blacklistCheck.status === 'error'
                        ? 'bg-orange-50 text-orange-700 border-orange-200'
                        : 'bg-blue-50 text-blue-700 border-blue-200'
                    }`}
                >
                  {blacklistCheck.status === 'checking' && (
                    <span className="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-current mr-2"></span>
                  )}
                  {blacklistCheck.text}
                </div>
              )}
            </div>

            <div>
              <label className={labelStyle}>Nome Completo *</label>
              <input
                type="text"
                name="nome"
                value={form.nome}
                onChange={handleChange}
                disabled={loadingState !== 'idle'}
                className={inputStyle}
                placeholder="Nome do indivíduo..."
                autoComplete="off"
              />
            </div>

            <div className="col-span-1 md:col-span-2">
              <label className={labelStyle}>Motivo do Bloqueio *</label>
              <select
                name="id_motivo"
                value={form.id_motivo}
                onChange={handleChange}
                disabled={loadingState !== 'idle'}
                className={inputStyle}
              >
                <option value="">Selecione o motivo...</option>
                {motivosLista.map(motivo => (
                  <option key={motivo.id_motivo} value={motivo.id_motivo}>
                    {motivo.descricao_motivo}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div
            className="flex justify-center mb-6"
            style={{ minHeight: '78px' }}
          >
            {showCaptcha && (
              <ReCAPTCHA
                key="captcha-blacklist"
                ref={captchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
                onChange={onCaptchaVerify}
              />
            )}
          </div>

          {message && (
            <div
              className={`mb-6 p-4 rounded-md text-center text-sm font-medium border shadow-sm flex items-center justify-center
                ${
                  message.type === 'success'
                    ? 'bg-green-50 text-green-700 border-green-200'
                    : message.type === 'error'
                    ? 'bg-red-50 text-red-700 border-red-200'
                    : 'bg-blue-50 text-blue-700 border-blue-200'
                }`}
            >
              {loadingState !== 'idle' && (
                <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-3"></span>
              )}
              {message.text}
            </div>
          )}

          {loadingState === 'idle' && (
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={limparCampos}
                className="w-full sm:w-1/3 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-3 rounded-lg font-semibold transition-colors"
              >
                Limpar
              </button>

              <button
                onClick={handleSubmit}
                disabled={
                  !recaptchaValido || blacklistCheck.status === 'listed'
                }
                className={`w-full sm:w-2/3 text-white px-4 py-3 rounded-lg font-bold transition-all shadow-md
                  ${
                    !recaptchaValido || blacklistCheck.status === 'listed'
                      ? 'bg-red-400 cursor-not-allowed'
                      : 'bg-red-600 hover:bg-red-700 hover:shadow-lg'
                  }`}
              >
                Registrar Bloqueio
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 text-center">
        <button
          onClick={() => {
            window.location.href = '/blacklist';
          }}
          className="px-4 py-2 text-md font-semibold text-red-500 transition-all duration-200 border border-red-500 rounded-lg hover:bg-red-500 hover:text-white"
        >
          Sair
        </button>
      </div>
    </div>
  );
}
